/** @format */

import { useContext, useEffect, useState } from 'react';
import { Box, Typography, Container, Popper } from '@mui/material';
import { COMPONENT_TITLE_SX } from '../homeStyles';
import dayjs from 'dayjs';
import { buildPayload } from '../../../Library/appfilter.lib';
import { FILTER_NAMES, useAppContext } from '../../../../store/store.app';
import {
  getCompPerformance,
  getPerformance,
} from '../../../../Services/service.home';
import { TABS_MAPPING } from '../KPI/KPIContainer';
import { HomeContext } from '../Home';
import { formatRevenue } from '../../../../Util/utils';
import useMaxDate from '../../BizBarometr/hooks/useMaxDate';
import { TouchApp } from '@mui/icons-material';

const COMP_SET = [
  {
    id: 'occ_str',
    title: 'Occ %',
  },
  {
    id: 'arr_str',
    title: 'ARR',
  },
  {
    id: 'revpar_str',
    title: 'RevPAR',
  },
  {
    id: 'mpi_str',
    title: 'MPI',
  },
  {
    id: 'ari_str',
    title: 'ARI',
  },
  {
    id: 'rgi_str',
    title: 'RGI',
  },
];

const FNB = [
  {
    id: 'covers',
    title: 'Covers',
  },
  {
    id: 'food_apc',
    title: 'APC Food',
  },
  {
    id: 'bev_apc',
    title: 'APC Beverage',
  },
  {
    id: 'revenue',
    title: 'Total F&B Rev',
  },
];

function getPerformanceColor(percentage) {
  const value = Number(percentage);

  if (!Number.isFinite(value)) {
    return '#374151';
  }

  if (value < -5) {
    return 'red';
  }

  if (value >= 0) {
    return 'green';
  }

  return 'orange';
}


function formatNumber(value, isRevenue = false, isOccupancy = false) {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  const cleanedValue =
    typeof value === 'string' ? value.replace(/,/g, '') : value;

  const numericValue = Number(cleanedValue);

  if (!Number.isFinite(numericValue)) {
    return '-';
  }

  if (isRevenue) {
    return (numericValue / 1000).toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  if (isOccupancy) {
    return numericValue.toLocaleString('en-IN', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
  }

  return numericValue.toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function getEmptyCompPerformance() {
  return [
    {
      rowName: 'Comp',
      cells: COMP_SET.slice(0, 3).map((metric) => ({
        id: metric.id,
        title: metric.title,
        value: 0,
      })),
    },
    {
      rowName: 'Hotel',
      cells: COMP_SET.slice(3).map((metric) => ({
        id: metric.id,
        title: metric.title,
        value: 0,
      })),
    },
  ];
}

function PerformanceStrip() {
  const [fnbPerformance, setFnbPerformance] = useState([]);
  const [compPerformance, setCompPerformance] = useState([]);
  const [compLoading, setCompLoading] = useState(false);

  const {
    appStore: { appFilters },
  } = useAppContext();

  const { activeTab, kpiData } = useContext(HomeContext);

  const { PARAMETER, CALC_DATERANGE } = appFilters;

  const maxDate = useMaxDate();

  const selectedEndDate = appFilters[FILTER_NAMES.DATERANGE]?.[0]?.endDate;

  const displayDate = selectedEndDate
    ? dayjs(selectedEndDate).format('DD/MM/YYYY')
    : maxDate
      ? maxDate.format('DD/MM/YYYY')
      : null;

  const payload = {
    ...buildPayload(appFilters),
  };

  function getMetricIdFromItem(item) {
    const rawUrl = item?.config?.url;

    if (!rawUrl) {
      return null;
    }

    const queryString = rawUrl.split('/').pop();

    if (!queryString) {
      return null;
    }

    const fromQuery = new URLSearchParams(queryString).get('name');

    if (fromQuery) {
      return fromQuery.replace(/restaurant_|banquet_|^total_/, '');
    }

    const cleaned = queryString.replace(/^\/+/, '').replace(/^total_/, '');

    if (FNB.some((metric) => metric.id === cleaned)) {
      return cleaned;
    }

    return queryString.replace(/restaurant_|banquet_/g, '') || null;
  }

  function getRow(data) {
    return data.reduce((acc, curr) => {
      if (!curr?.data) {
        return acc;
      }

      const id = getMetricIdFromItem(curr);

      if (!id) {
        return acc;
      }

      const metric = FNB.find((f) => f.id === id);

      if (!metric) {
        return acc;
      }

      const {
        value,
        budget,
        forecast,
        last_year,
        budget_percentage,
        forecast_percentage,
        last_yr_percentage,
      } = curr.data;

      return [
        ...acc,
        {
          id,
          title: metric.title,
          value,
          budget,
          forecast,
          last_year,
          budget_percentage,
          forecast_percentage,
          last_yr_percentage,
        },
      ];
    }, []);
  }

  function getTotalFnbRows() {
    const totalMetrics = [
      {
        id: 'covers',
        title: 'Covers',
      },
      {
        id: 'food_apc',
        title: 'APC Food',
      },
      {
        id: 'bev_apc',
        title: 'APC Beverage',
      },
      {
        id: 'fnb_revenue',
        title: 'Total F&B Rev',
      },
    ];

    return totalMetrics
      .map((metric) => {
        const apiData = kpiData?.[metric.id];

        if (!apiData) {
          return null;
        }

        return {
          id: metric.id,
          title: metric.title,
          value: apiData.value,
          forecast: apiData.forecast,
          budget: apiData.budget,
          last_year: apiData.last_year,
          forecast_percentage: apiData.forecast_percentage,
          budget_percentage: apiData.budget_percentage,
          last_yr_percentage: apiData.last_yr_percentage,
        };
      })
      .filter(Boolean);
  }

  async function fetchFNBData() {
    try {
      const restaurantPromises = FNB.map((f) =>
        getPerformance(
          `restaurant_${f.id}`,
          TABS_MAPPING[activeTab],
          payload
        )
      );

      const banquetPromises = FNB.map((f) =>
        getPerformance(
          `banquet_${f.id}`,
          TABS_MAPPING[activeTab],
          payload
        )
      );

      const [restaurantRaw, banquetRaw] = await Promise.all([
        Promise.all(restaurantPromises),
        Promise.all(banquetPromises),
      ]);

      const restaurantResults = getRow(restaurantRaw);
      const banquetResults = getRow(banquetRaw);

      setFnbPerformance([
        {
          rowName: 'F&B Outlets',
          cells: restaurantResults,
        },
        {
          rowName: 'Banquet',
          cells: banquetResults,
        },
      ]);
    } catch (error) {
      console.error('Error fetching F&B performance:', error);
    }
  }

  async function fetchCompsetData() {
    setCompLoading(true);

    try {
      const compSetPromises = COMP_SET.map((f) =>
        getCompPerformance(
          f.id,
          TABS_MAPPING[activeTab],
          payload
        )
      );

      const compSetResults = await Promise.all(compSetPromises);

      const formattedResults = compSetResults.map((item, index) => {
        let title = COMP_SET[index]?.title;
        let metricId = COMP_SET[index]?.id;

        try {
          const baseURL = item?.config?.baseURL || '';
          const apiUrl = item?.config?.url || '';

          if (baseURL || apiUrl) {
            const url = new URL(
              baseURL + apiUrl,
              window.location.origin
            );

            const apiMetricId = url.searchParams.get('name');

            const metric = COMP_SET.find(
              (c) => c.id === apiMetricId
            );

            if (metric) {
              title = metric.title;
              metricId = metric.id;
            }
          }
        } catch (error) {
          console.warn(
            'Unable to parse Comp Set API URL:',
            error
          );
        }

        let value = Number(item?.data?.value);

        if (!Number.isFinite(value)) {
          value = 0;
        }

        const isOccupancy = /Occ/gi.test(title || '');

        const formattedValue = formatNumber(
          value,
          false,
          isOccupancy
        );

        return {
          id: metricId,
          title,
          value: formattedValue,
        };
      });

      setCompPerformance([
        {
          rowName: 'Comp',
          cells: formattedResults.slice(0, 3),
        },
        {
          rowName: 'Hotel',
          cells: formattedResults.slice(3),
        },
      ]);
    } catch (error) {
      console.error(
        'Error fetching Comp Set performance:',
        error
      );

      setTimeout(() => {
        setCompPerformance(getEmptyCompPerformance());
      }, 1000);
    } finally {
      setCompLoading(false);
    }
  }

  useEffect(() => {
    if (!PARAMETER || !CALC_DATERANGE) {
      return;
    }

    fetchFNBData();
  }, [PARAMETER, CALC_DATERANGE, activeTab]);

  useEffect(() => {
    if (!PARAMETER || !CALC_DATERANGE) {
      return;
    }

    fetchCompsetData();
  }, [PARAMETER, CALC_DATERANGE, activeTab]);

  const totalFnbRows = getTotalFnbRows();

  const finalFnbRows =
    totalFnbRows.length > 0
      ? [
        ...fnbPerformance,
        {
          rowName: 'Total',
          cells: totalFnbRows,
        },
      ]
      : fnbPerformance;

  return (
    <Container
      maxWidth="lg"
      sx={{
        mt: 0,
        px: 0,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Typography
          fontSize={12}
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
          }}
          color="text.secondary"
        >
          Revenue in &#39;000
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'minmax(0, 1fr) minmax(0, 1fr)',
            },
            columnGap: 4,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <PerformanceTable
            title="Rooms Comp Set Performance"
            rows={compPerformance}
            maxDate={displayDate}
            loading={compLoading}
          />

          <PerformanceTable
            title="F&B Performance"
            rows={finalFnbRows}
            showTooltip
          />
        </Box>
      </Box>
    </Container>
  );
}

function PerformanceTable({
  rows,
  title,
  maxDate = '',
  showTooltip = false,
  loading = false,
}) {
  const isFnb = title === 'F&B Performance';

  return (
    <Box
      sx={{
        mt: 2,
        width: '100%',
        minWidth: 0,
        position: 'relative',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          minHeight: 32,
        }}
      >
        {showTooltip && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
            }}
          >
            <ColorCodeTooltip />
          </Box>
        )}

        <Typography
          color="text.disabled"
          variant="h6"
          sx={{
            textAlign: 'center',
            flexGrow: 1,
            ...COMPONENT_TITLE_SX,
            minWidth: 0,
            overflowWrap: 'anywhere',
          }}
        >
          {title} {maxDate && 'as on ' + maxDate}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#fff',
          width: '100%',
          minWidth: 0,
        }}
      >
        {rows.map((row, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              width: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
              border: '1px solid silver',
              px: 2,
              py: 1,
              borderRadius: 2,
              opacity: loading ? 0.6 : 1,
              transition: 'opacity 0.2s ease',
            }}
            aria-label={row.rowName}
          >
            <Typography
              sx={{
                mr: 2,
                transform: 'rotate(-90deg)',
                textAlign: 'center',
                fontSize: '0.9rem',
                width: '20px',
                minWidth: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {row.rowName}
            </Typography>

            {row.cells.map((cell, cellIndex) => {
              const title = cell.title || '';

              const isRevenue = /Rev$/i.test(title);

              const isOccupancy = /Occ/gi.test(title);

              const formattedValue =
                typeof cell.value === 'number' ||
                  (typeof cell.value === 'string' &&
                    cell.value !== '')
                  ? formatNumber(
                    cell.value,
                    isRevenue,
                    isOccupancy
                  )
                  : cell.value ?? '-';

              return (
                <Box
                  key={cellIndex}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    py: 1,
                    position: 'relative',
                    flex: '1 1 0',
                    minWidth: 0,
                    borderLeft: '1px solid silver',
                  }}
                  aria-label={cell.title}
                >
                  <Typography
                    sx={{
                      textAlign: 'center',
                      fontSize: {
                        xs: '0.72rem',
                        sm: '0.825rem',
                      },
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {cell.title}
                  </Typography>

                  {isFnb ? (
                    <Typography
                      component="span"
                      sx={{
                        textAlign: 'center',
                        fontSize: {
                          xs: '0.78rem',
                          sm: '0.875rem',
                        },
                        fontWeight: 500,
                        color: getPerformanceColor(
                          cell.budget_percentage
                        ),
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span className="data-number">
                        {formattedValue}
                      </span>
                    </Typography>
                  ) : (
                    <Typography
                      component="span"
                      sx={{
                        textAlign: 'center',
                        fontSize: {
                          xs: '0.78rem',
                          sm: '0.875rem',
                        },
                        fontWeight: 500,
                        color: 'text.disabled',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span className="data-number">
                        {formattedValue}
                      </span>
                    </Typography>
                  )}

                  {isFnb && <Tooltip data={cell} />}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function ColorCodeTooltip() {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMouseEnter = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        sx={{
          color: 'text.disabled',
          cursor: 'pointer',
          zIndex: 1,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <TouchApp
          fontSize="small"
          sx={{
            color: '#757575',
          }}
        />
      </Box>

      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="right-start"
        sx={{
          zIndex: 9999,
        }}
      >
        <Box
          sx={{
            minWidth: 'fit-content',
            backgroundColor: '#e8e4e4',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 1,
            zIndex: 1300,
          }}
        >
          <Box sx={{ p: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 'bold',
                mb: 1,
              }}
            >
              F&B Performance Information
            </Typography>

            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 'medium',
                  mb: 0.5,
                }}
              >
                Color Codes:
              </Typography>

              <Box sx={{ pl: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.75rem',
                  }}
                >
                  🔴 Red: Below 95%
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.75rem',
                  }}
                >
                  🟡 Amber: 95% - 100%
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.75rem',
                  }}
                >
                  🟢 Green: Above 100%
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Popper>
    </>
  );
}

function TooltipRow({
  label,
  value,
  percentage,
  title,
}) {
  const numericPercentage = Number(percentage);

  const hasPercentage =
    percentage !== null &&
    percentage !== undefined &&
    percentage !== '' &&
    Number.isFinite(numericPercentage);

  const color = hasPercentage
    ? getPerformanceColor(numericPercentage)
    : '#374151';

  const isRevenue = /Rev$/i.test(title || '');

  const isOccupancy = /Occ/gi.test(title || '');

  const formattedValue = formatNumber(
    value,
    isRevenue,
    isOccupancy
  );

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns:
          'minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1fr)',
        gap: 1,
        width: '100%',
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: '0.75rem',
        }}
      >
        {label}
      </Typography>

      <Typography
        component="span"
        sx={{
          fontSize: '0.75rem',
          textAlign: 'right',
        }}
      >
        <span className="data-number">
          {formattedValue}
        </span>
      </Typography>

      <Typography
        sx={{
          color,
          fontSize: '0.75rem',
          textAlign: 'right',
        }}
      >
        {hasPercentage
          ? `${numericPercentage.toFixed(1)}%`
          : ''}
      </Typography>
    </Box>
  );
}

function Tooltip({ data }) {
  const [anchorEl, setAnchorEl] = useState(null);

  if (!data) {
    return null;
  }

  const handleMouseEnter = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          right: 1,
          bottom: 1,
          color: 'text.disabled',
          cursor: 'pointer',
          zIndex: 1000,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <TouchApp
          fontSize="small"
          sx={{
            color: '#757575',
          }}
        />
      </Box>

      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="top"
        sx={{
          zIndex: 9999,
        }}
      >
        <Box
          sx={{
            minWidth: 'fit-content',
            backgroundColor: '#e8e4e4',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 1,
          }}
        >
          <TooltipRow
            label="Actual"
            title={data.title}
            value={data.value}
            percentage=""
          />

          <TooltipRow
            label="Forecast"
            title={data.title}
            value={data.forecast}
            percentage={data.forecast_percentage}
          />

          <TooltipRow
            label="Budget"
            title={data.title}
            value={data.budget}
            percentage={data.budget_percentage}
          />

          <TooltipRow
            label="LY"
            title={data.title}
            value={data.last_year}
            percentage={data.last_yr_percentage}
          />
        </Box>
      </Popper>
    </>
  );
}

export default PerformanceStrip;
